import { useEffect, useState, useCallback } from 'react';
import { useMedplum, useMedplumProfile, useSubscription } from '@medplum/react';
import { DocumentReference, Annotation } from '@medplum/fhirtypes';
import { ARKON } from '@consts/systems.consts';
import { formatHumanName } from '@medplum/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
import { usePatient } from '@/hooks/usePatient';
import { UPDATE_ONLY_OPTIONS } from '@/consts/subscription.consts';

// -----------------------------------------------------------------------------
// Temporary ID for new notes that haven't been saved yet
const TEMP_NOTE_ID = '__temp_new_note__';

// -----------------------------------------------------------------------------
export interface PatientAnnotationNote {
  id?: string;
  content: string;
  authorName?: string;
  authorReference?: string;
  timestamp: string;
  encounterReference?: string;
}

// -----------------------------------------------------------------------------
export const NOTE_CATEGORY = {
  coding: [
    {
      system: ARKON.CS.PATIENT_NOTES.BASE,
      code: ARKON.CS.PATIENT_NOTES.CODES.CLINICAL_NOTE,
      display: 'Clinical Note',
    },
  ],
};

// -----------------------------------------------------------------------------
const encodeBase64 = (text: string): string => {
  const encoder = new TextEncoder();
  const uint8Array = encoder.encode(text);
  const binaryString = Array.from(uint8Array)
    .map((byte) => String.fromCharCode(byte))
    .join('');
  return btoa(binaryString);
};

// -----------------------------------------------------------------------------
const decodeBase64 = (base64: string): string => {
  const binaryString = atob(base64);
  const uint8Array = new Uint8Array(
    Array.from(binaryString).map((char) => char.charCodeAt(0))
  );
  const decoder = new TextDecoder();
  return decoder.decode(uint8Array);
};

// -----------------------------------------------------------------------------
export const usePatientNotes = () => {
  const [notes, setNotes] = useState<PatientAnnotationNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Track the note currently being edited (note ID, temp ID, or null)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  // Local content for the note being edited (before it's saved)
  const [editingContent, setEditingContent] = useState('');

  const patient = usePatient();
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const { currentEncounter } = useCurrentEncounter(patient);

  // Helper to check if we're creating a new note
  const isCreatingNew = editingNoteId === TEMP_NOTE_ID;

  // ---------------------------------------------------------------------------
  // Helper function to parse DocumentReference to PatientAnnotationNote
  const parseDocumentReferenceToNote = useCallback(
    (docRef: DocumentReference): PatientAnnotationNote | null => {
      // Check if this is a patient note by category
      const isPatientNote = docRef.category?.some((cat) =>
        cat.coding?.some(
          (coding) =>
            coding.system === ARKON.CS.PATIENT_NOTES.BASE &&
            coding.code === ARKON.CS.PATIENT_NOTES.CODES.CLINICAL_NOTE
        )
      );

      if (!isPatientNote) {
        return null;
      }

      const encodedContent = docRef.content?.[0]?.attachment?.data || '';
      const decodedContent = encodedContent ? decodeBase64(encodedContent) : '';

      // Parse the annotations stored in the extension
      const annotationExt = docRef.extension?.find(
        (ext) => ext.url === `${ARKON.BASE}/annotation`
      );

      let annotation: Annotation | undefined;
      if (annotationExt?.valueString) {
        try {
          annotation = JSON.parse(annotationExt.valueString) as Annotation;
        } catch (error) {
          console.error('Error parsing annotation:', error);
        }
      }

      return {
        id: docRef.id,
        content: decodedContent,
        authorName: annotation?.authorString || 'Unknown',
        authorReference: annotation?.authorReference?.reference,
        timestamp: annotation?.time || docRef.date || new Date().toISOString(),
        encounterReference: docRef.context?.encounter?.[0]?.reference,
      };
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Fetch all notes for the patient
  const fetchNotes = useCallback(async () => {
    if (!patient?.id) {
      setNotes([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await medplum.search('DocumentReference', {
        patient: patient.id,
        category: `${ARKON.CS.PATIENT_NOTES.BASE}|${ARKON.CS.PATIENT_NOTES.CODES.CLINICAL_NOTE}`,
        _sort: '-date',
        _count: 100,
      });

      if (response.entry && response.entry.length > 0) {
        const parsedNotes: PatientAnnotationNote[] = response.entry
          .map((entry) => {
            const docRef = entry.resource as DocumentReference;
            return parseDocumentReferenceToNote(docRef);
          })
          .filter((note): note is PatientAnnotationNote => note !== null);

        setNotes(parsedNotes);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('Error fetching patient notes:', error);
      setNotes([]);
    } finally {
      setIsLoading(false);
    }
  }, [patient, medplum, parseDocumentReferenceToNote]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // ---------------------------------------------------------------------------
  // Subscription for DocumentReference changes (create, update, delete)
  useSubscription(
    patient?.id
      ? `DocumentReference?patient=Patient/${patient.id}&category=${ARKON.CS.PATIENT_NOTES.BASE}|${ARKON.CS.PATIENT_NOTES.CODES.CLINICAL_NOTE}`
      : '',
    (bundle) => {
      const docRef = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'DocumentReference'
      )?.resource as DocumentReference | undefined;

      if (!docRef?.id) {
        return;
      }

      // Check if this is a delete operation by checking status
      if (
        docRef.status === 'superseded' ||
        docRef.status === 'entered-in-error'
      ) {
        // Remove the note
        setNotes((prev) => {
          // Don't remove if we're currently editing this note
          if (editingNoteId === docRef.id) {
            return prev;
          }
          return prev.filter((n) => n.id !== docRef.id);
        });
        return;
      }

      // Parse the DocumentReference to a note
      const note = parseDocumentReferenceToNote(docRef);
      if (!note) {
        return;
      }

      // Handle create or update
      setNotes((prev) => {
        // Don't update if we're currently editing this note (to avoid overwriting user input)
        if (editingNoteId === docRef.id) {
          return prev;
        }

        const exists = prev.find((n) => n.id === docRef.id);
        if (exists) {
          // Update existing note
          return prev.map((n) => (n.id === docRef.id ? note : n));
        } else {
          // Add new note at the beginning (most recent first)
          return [note, ...prev];
        }
      });
    },
    UPDATE_ONLY_OPTIONS
  );

  // ---------------------------------------------------------------------------
  // Save note (create if temp ID, update if real ID)
  const saveNote = useDebouncedCallback(
    async (noteId: string, content: string) => {
      if (!patient?.id || !profile || !content.trim()) {
        return;
      }

      const isNew = noteId === TEMP_NOTE_ID;

      setIsSaving(true);
      try {
        const encodedContent = encodeBase64(content);
        const now = new Date().toISOString();
        const authorName = formatHumanName(profile.name?.[0]);

        // Create annotation object
        const annotation: Annotation = {
          authorReference: {
            reference: `${profile.resourceType}/${profile.id}`,
            display: authorName,
          },
          authorString: authorName,
          time: now,
          text: content,
        };

        if (!isNew) {
          // Update existing note
          const docRef = await medplum.readResource(
            'DocumentReference',
            noteId
          );

          const updatedDocRef: DocumentReference = {
            ...docRef,
            content: [
              {
                attachment: {
                  contentType: 'text/plain',
                  data: encodedContent,
                },
              },
            ],
            extension: docRef.extension?.map((ext) =>
              ext.url === `${ARKON.BASE}/annotation`
                ? { ...ext, valueString: JSON.stringify(annotation) }
                : ext
            ),
          };

          await medplum.updateResource(updatedDocRef);

          // Update local state (no refetch needed)
          setNotes((prev) =>
            prev.map((note) =>
              note.id === noteId
                ? {
                    ...note,
                    content,
                    authorName,
                  }
                : note
            )
          );
        } else {
          // Create new note
          const newNote: DocumentReference = {
            resourceType: 'DocumentReference',
            status: 'current',
            type: {
              coding: [
                {
                  system: ARKON.SD.GENERAL_NOTES,
                  code: 'notes',
                  display: 'Note',
                },
              ],
            },
            category: [NOTE_CATEGORY],
            subject: {
              reference: `Patient/${patient.id}`,
              display: patient.name?.[0]?.text || `Patient ${patient.id}`,
            },
            description: 'Clinical Annotation',
            content: [
              {
                attachment: {
                  contentType: 'text/plain',
                  data: encodedContent,
                },
              },
            ],
            date: now,
            extension: [
              {
                url: `${ARKON.BASE}/annotation`,
                valueString: JSON.stringify(annotation),
              },
            ],
            // Link to encounter if one is in progress
            ...(currentEncounter?.id && {
              context: {
                encounter: [
                  {
                    reference: `Encounter/${currentEncounter.id}`,
                  },
                ],
              },
            }),
          };

          const savedNote = await medplum.createResource(newNote);

          if (savedNote.id) {
            // Replace temp note with real note in the array (keep same position)
            setNotes((prev) =>
              prev.map((note) =>
                note.id === TEMP_NOTE_ID
                  ? {
                      id: savedNote.id,
                      content,
                      authorName,
                      authorReference: `${profile.resourceType}/${profile.id}`,
                      timestamp: savedNote.date || now,
                      encounterReference: currentEncounter?.id
                        ? `Encounter/${currentEncounter.id}`
                        : undefined,
                    }
                  : note
              )
            );

            // Update editingNoteId to the real ID
            setEditingNoteId(savedNote.id);
          }
        }
      } catch (error) {
        console.error('Error saving patient note:', error);
      } finally {
        setIsSaving(false);
      }
    },
    1000
  );

  // ---------------------------------------------------------------------------
  // Start creating a new note
  const startNewNote = () => {
    const authorName = formatHumanName(profile?.name?.[0]);

    // Add a temporary note at the beginning of the array
    const tempNote: PatientAnnotationNote = {
      id: TEMP_NOTE_ID,
      content: '',
      authorName,
      authorReference: profile
        ? `${profile.resourceType}/${profile.id}`
        : undefined,
      timestamp: new Date().toISOString(),
      encounterReference: currentEncounter?.id
        ? `Encounter/${currentEncounter.id}`
        : undefined,
    };

    setNotes((prev) => [tempNote, ...prev]);
    setEditingNoteId(TEMP_NOTE_ID);
    setEditingContent('');
  };

  // ---------------------------------------------------------------------------
  // Start editing an existing note
  const startEditingNote = (noteId: string) => {
    const note = notes.find((n) => n.id === noteId);
    setEditingNoteId(noteId);
    setEditingContent(note?.content || '');
  };

  // ---------------------------------------------------------------------------
  // Update content of the note being edited (triggers auto-save)
  const updateEditingContent = (content: string) => {
    setEditingContent(content);

    // Also update the note in the array for display purposes
    if (editingNoteId) {
      setNotes((prev) =>
        prev.map((note) =>
          note.id === editingNoteId ? { ...note, content } : note
        )
      );

      saveNote(editingNoteId, content);
    }
  };

  // ---------------------------------------------------------------------------
  // Stop editing (close edit mode)
  const stopEditing = () => {
    // If we're stopping edit on a temp note that was never saved, remove it
    if (editingNoteId === TEMP_NOTE_ID) {
      const tempNote = notes.find((n) => n.id === TEMP_NOTE_ID);
      if (!tempNote?.content.trim()) {
        setNotes((prev) => prev.filter((note) => note.id !== TEMP_NOTE_ID));
      }
    }

    setEditingNoteId(null);
    setEditingContent('');
  };

  // ---------------------------------------------------------------------------
  // Delete a note
  const deleteNote = async (noteId: string): Promise<void> => {
    // If deleting the temp note, just remove it from state
    if (noteId === TEMP_NOTE_ID) {
      setNotes((prev) => prev.filter((note) => note.id !== TEMP_NOTE_ID));
      if (editingNoteId === TEMP_NOTE_ID) {
        setEditingNoteId(null);
        setEditingContent('');
      }
      return;
    }

    setIsSaving(true);
    try {
      await medplum.deleteResource('DocumentReference', noteId);

      // Update local state (remove the deleted note)
      setNotes((prev) => prev.filter((note) => note.id !== noteId));

      if (editingNoteId === noteId) {
        setEditingNoteId(null);
        setEditingContent('');
      }
    } catch (error) {
      console.error('Error deleting patient note:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  return {
    notes,
    isLoading,
    isSaving,
    isCreatingNew,
    editingNoteId,
    editingContent,
    startNewNote,
    startEditingNote,
    updateEditingContent,
    stopEditing,
    deleteNote,
    refetchNotes: fetchNotes,
  };
};
